import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import logoUrl from '@/assets/logo.png';

export function LoginScreen() {
  const { login, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login(email, password);
    } catch {
      /* error is surfaced via store */
    }
  };

  // Layout rationale: Excel taskpane heights vary wildly (200–900+ px)
  // depending on user docking. We make the OUTER container scrollable
  // (overflow-y-auto) and use min-h-full + flex justify-center INSIDE,
  // so content centers vertically when the pane is tall but falls back
  // to natural scroll when the pane is short. Logo and every field
  // stay reachable on every form factor — desktop wide, taskpane tall,
  // taskpane short — without anything ever clipping.
  return (
    <div className="h-full w-full overflow-y-auto overscroll-contain">
      <div className="flex min-h-full w-full flex-col items-center justify-center gap-5 px-4 py-6 sm:gap-6 sm:p-6">
        <div className="flex w-full max-w-sm flex-col items-center gap-3 text-center">
          <img
            src={logoUrl}
            alt="Ira Skills"
            className="h-9 w-auto max-w-[200px] object-contain sm:h-10 sm:max-w-[220px]"
          />
          <p className="text-sm font-medium text-text-primary dark:text-text-primary-dark">
            AI assistant for Excel — generate formulas, build charts, create pivots,
            and clean data using natural-language chat.
          </p>
          <p className="text-xs text-text-secondary">
            Works in English, Hindi, and Hinglish.
          </p>
          <p className="text-xs text-text-secondary">
            Enterprise add-in. Sign in with credentials provided by your organization.
          </p>
        </div>

        <form onSubmit={onSubmit} className="card w-full max-w-sm p-4 space-y-3 sm:p-5 sm:space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-text-secondary">Email</label>
            <input
              type="email"
              autoComplete="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-text-secondary">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                className="input pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute inset-y-0 right-2 flex items-center text-text-secondary hover:text-text-primary"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log in'}
          </button>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <a
              href="#forgot-password"
              className="text-primary hover:underline"
            >
              Forgot password?
            </a>
            <span className="text-text-secondary">Contact admin for access</span>
          </div>
        </form>
      </div>
    </div>
  );
}
